import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

import {
  ICreateAjusteMovimentacaoBody,
  ICreateEntradaEstoqueBody,
  ICreateSaidaEstoqueBody,
  IEstoqueRelatorioFilter,
  IEstoqueRelatorioMovimentacaoListResponse,
  IEstoqueRelatorioPosicaoItem,
  IEstoqueRelatorioVendaItem,
  IGetDashboardEstoqueParams,
  IGetDashboardEstoqueResponse,
  IGetEstoquePaginatedParams,
  IGetInventarioParams,
  IGetMovimentacoesPaginatedParams,
  IGetReportBase64,
  IGetReportEstoqueExcelParams,
  IGetVendasPaginatedParams,
  IInventarioItem,
  IMovimentacaoEstoqueDto,
  IPaginationResponse,
  IEstoqueItem,
  IMovimentacaoEstoqueItem,
  IVendaEstoquePaginatedItem
} from './estoque.model';

@Injectable({
  providedIn: 'root'
})
export class EstoqueService {
  private readonly baseUrl = `${environment.baseURL}Estoque/`;

  constructor(private http: HttpClient) {}

  GetEstoquePaginated(
    params: IGetEstoquePaginatedParams
  ): Observable<IPaginationResponse<IEstoqueItem>> {
    return this.http.get<IPaginationResponse<IEstoqueItem>>(
      `${this.baseUrl}GetEstoquePaginated`,
      { params: this.toHttpParams({ PAGE_NUMBER: 1, PAGE_SIZE: 8, ...params }) }
    );
  }

  GetMovimentacoesPaginated(
    params: IGetMovimentacoesPaginatedParams
  ): Observable<IPaginationResponse<IMovimentacaoEstoqueItem>> {
    return this.http.get<IPaginationResponse<IMovimentacaoEstoqueItem>>(
      `${this.baseUrl}GetMovimentacoesPaginated`,
      { params: this.toHttpParams({ PAGE_NUMBER: 1, PAGE_SIZE: 8, ...params }) }
    );
  }

  GetInventario(params?: IGetInventarioParams): Observable<IInventarioItem[]> {
    return this.http.get<IInventarioItem[]>(`${this.baseUrl}GetInventario`, {
      params: this.toHttpParams(params ?? {})
    });
  }

  GetDashboard(
    params: IGetDashboardEstoqueParams
  ): Observable<IGetDashboardEstoqueResponse> {
    return this.http.get<IGetDashboardEstoqueResponse>(
      `${this.baseUrl}GetDashboard`,
      { params: this.toHttpParams(params) }
    );
  }

  GetRelatorioVendas(
    filter: IEstoqueRelatorioFilter
  ): Observable<IEstoqueRelatorioVendaItem[]> {
    return this.http.get<IEstoqueRelatorioVendaItem[]>(
      `${this.baseUrl}GetRelatorioVendas`,
      { params: this.toHttpParams(filter) }
    );
  }

  GetRelatorioMovimentacoes(
    filter: IEstoqueRelatorioFilter
  ): Observable<IEstoqueRelatorioMovimentacaoListResponse> {
    return this.http.get<IEstoqueRelatorioMovimentacaoListResponse>(
      `${this.baseUrl}GetRelatorioMovimentacoes`,
      { params: this.toHttpParams(filter) }
    );
  }

  GetRelatorioPosicao(
    filter: IEstoqueRelatorioFilter
  ): Observable<IEstoqueRelatorioPosicaoItem[]> {
    return this.http.get<IEstoqueRelatorioPosicaoItem[]>(
      `${this.baseUrl}GetRelatorioPosicao`,
      { params: this.toHttpParams(filter) }
    );
  }

  CreateEntrada(body: ICreateEntradaEstoqueBody): Observable<IMovimentacaoEstoqueDto> {
    return this.http.post<IMovimentacaoEstoqueDto>(
      `${this.baseUrl}CreateEntrada`,
      body
    );
  }

  CreateSaida(body: ICreateSaidaEstoqueBody): Observable<IMovimentacaoEstoqueDto> {
    return this.http.post<IMovimentacaoEstoqueDto>(
      `${this.baseUrl}CreateSaida`,
      body
    );
  }

  CreateAjusteMovimentacao(body: ICreateAjusteMovimentacaoBody): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}CreateAjusteMovimentacao`, body);
  }

  GetVendasPaginated(
    params: IGetVendasPaginatedParams
  ): Observable<IPaginationResponse<IVendaEstoquePaginatedItem>> {
    return this.http.get<IPaginationResponse<IVendaEstoquePaginatedItem>>(
      `${this.baseUrl}GetVendasPaginated`,
      { params: this.toHttpParams({ PAGE_NUMBER: 1, PAGE_SIZE: 8, ...params }) }
    );
  }

  GetReportVendasPorClientePdf(
    filter: IEstoqueRelatorioFilter
  ): Observable<IGetReportBase64> {
    return this.http.get<IGetReportBase64>(
      `${this.baseUrl}GetReportVendasPorClientePdf`,
      { params: this.toHttpParams(filter) }
    );
  }

  GetReportMovimentacoesPdf(
    filter: IEstoqueRelatorioFilter
  ): Observable<IGetReportBase64> {
    return this.http.get<IGetReportBase64>(
      `${this.baseUrl}GetReportMovimentacoesPdf`,
      { params: this.toHttpParams(filter) }
    );
  }

  GetReportExcel(params: IGetReportEstoqueExcelParams): Observable<IGetReportBase64> {
    const httpParams: Record<string, string | number | boolean> = {
      ReportType: params.ReportType
    };

    if (params.ReportType === 'estoque') {
      if (params.NM_PROD) httpParams['EstoqueFilter.NM_PROD'] = params.NM_PROD;
      if (params.FL_ESTQ_BX !== undefined && params.FL_ESTQ_BX !== null) {
        httpParams['EstoqueFilter.FL_ESTQ_BX'] = params.FL_ESTQ_BX;
      }
      if (params.FL_EM_ESTQ !== undefined && params.FL_EM_ESTQ !== null) {
        httpParams['EstoqueFilter.FL_EM_ESTQ'] = params.FL_EM_ESTQ;
      }
    }

    if (params.ReportType === 'inventario') {
      if (params.FL_ESTQ_BX !== undefined && params.FL_ESTQ_BX !== null) {
        httpParams['InventarioFilter.FL_ESTQ_BX'] = params.FL_ESTQ_BX;
      }
      if (params.FL_EM_ESTQ !== undefined && params.FL_EM_ESTQ !== null) {
        httpParams['InventarioFilter.FL_EM_ESTQ'] = params.FL_EM_ESTQ;
      }
      if (params.DT_INIC) httpParams['InventarioFilter.DT_INIC'] = params.DT_INIC;
      if (params.DT_FIM) httpParams['InventarioFilter.DT_FIM'] = params.DT_FIM;
    }

    if (params.ReportType === 'movimentacoes' || params.ReportType === 'vendas' || params.ReportType === 'posicao') {
      if (params.DT_INIC) httpParams['RelatorioFilter.DT_INIC'] = params.DT_INIC;
      if (params.DT_FIM) httpParams['RelatorioFilter.DT_FIM'] = params.DT_FIM;
      if (params.FK_PROD) httpParams['RelatorioFilter.FK_PROD'] = params.FK_PROD;
      if (params.FL_CNCL !== undefined && params.FL_CNCL !== null) {
        httpParams['RelatorioFilter.FL_CNCL'] = params.FL_CNCL;
      }
      if (params.NU_TIPO_MOVM) httpParams['RelatorioFilter.NU_TIPO_MOVM'] = params.NU_TIPO_MOVM;
      if (params.NU_ORIG_MOVM) httpParams['RelatorioFilter.NU_ORIG_MOVM'] = params.NU_ORIG_MOVM;
    }

    return this.http.get<IGetReportBase64>(`${this.baseUrl}GetReportExcel`, {
      params: httpParams
    });
  }

  private toHttpParams(params: object): HttpParams {
    let httpParams = new HttpParams();
    Object.entries(params as Record<string, unknown>).forEach(([key, value]) => {
      if (
        value === null ||
        value === undefined ||
        value === '' ||
        (typeof value === 'number' && Number.isNaN(value))
      ) {
        return;
      }
      httpParams = httpParams.set(key, String(value));
    });
    return httpParams;
  }
}
